/**
 * SkillsStore - MobX store for skill management.
 *
 * Handles XP gain, leveling, action execution, and affinity tracking.
 * Integrates with CharacterStore for persistence and NotificationStore for feedback.
 */

import {
  getLevelFromXP,
  getProgressToNextLevel,
  getXPToNextLevel,
  calculateAffinityPercent,
  MAX_AFFINITY_COMPLETIONS,
  createSkillingActivityData,
  createSkillingActivity,
  type SkillProgress,
  type ActionAffinity,
  type ActionDefinition,
  type SkillDefinition,
} from '@idle-depths/shared';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

/**
 * Result of validating an action.
 */
interface ActionValidationResult {
  canPerform: boolean;
  reason?: string;
}

/**
 * SkillsStore manages all skill-related state and operations.
 *
 * Responsibilities:
 * - Add XP to skills and handle level-ups
 * - Start and stop skilling actions
 * - Track action affinity (completions toward mastery)
 * - Validate action requirements
 *
 * @example
 * ```tsx
 * const { skillsStore } = useRootStore();
 *
 * // Start mining copper
 * skillsStore.startAction('mining', 'mine_copper');
 *
 * // Add XP (called by SkillsSystem on action completion)
 * skillsStore.addXP('mining', 10);
 *
 * // Get skill info
 * const level = skillsStore.getSkillLevel('mining');
 * ```
 */
export class SkillsStore {
  /** Reference to root store */
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  // ===========================================================================
  // Computed Properties
  // ===========================================================================

  /**
   * Get active character's skills.
   *
   * Note: Skills migration is handled by CharacterStore.loadFromLocalStorage
   */
  get activeCharacterSkills(): Record<string, SkillProgress> | null {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) return null;

    return character.skills;
  }

  /**
   * Get skill progress for a specific skill.
   *
   * @param skillId - The skill ID
   * @returns SkillProgress or null if not found
   */
  getSkillProgress(skillId: string): SkillProgress | null {
    return this.activeCharacterSkills?.[skillId] ?? null;
  }

  /**
   * Get current level for a skill.
   *
   * @param skillId - The skill ID
   * @returns Current level (1 if not found)
   */
  getSkillLevel(skillId: string): number {
    return this.getSkillProgress(skillId)?.level ?? 1;
  }

  /**
   * Get total XP for a skill.
   *
   * @param skillId - The skill ID
   * @returns Total XP (0 if not found)
   */
  getSkillXP(skillId: string): number {
    return this.getSkillProgress(skillId)?.totalXP ?? 0;
  }

  /**
   * Get progress to next level (0-1).
   *
   * @param skillId - The skill ID
   * @returns Progress percentage (0 if not found)
   */
  getSkillProgressPercent(skillId: string): number {
    const skill = this.getSkillProgress(skillId);
    if (!skill) return 0;
    return getProgressToNextLevel(skill.totalXP);
  }

  /**
   * Get XP remaining to next level.
   *
   * @param skillId - The skill ID
   * @returns XP needed (0 if not found or at max)
   */
  getXPToNextLevel(skillId: string): number {
    const skill = this.getSkillProgress(skillId);
    if (!skill) return 0;
    return getXPToNextLevel(skill.totalXP);
  }

  /**
   * Check if a skill is unlocked.
   *
   * @param skillId - The skill ID
   * @returns True if unlocked
   */
  isSkillUnlocked(skillId: string): boolean {
    return this.getSkillProgress(skillId)?.unlocked ?? false;
  }

  /**
   * Get action affinity for a specific action.
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   * @returns ActionAffinity or null if not found
   */
  getActionAffinity(skillId: string, actionId: string): ActionAffinity | null {
    const skill = this.getSkillProgress(skillId);
    return skill?.actionAffinity[actionId] ?? null;
  }

  /**
   * Get affinity percentage for an action (0-100).
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   * @returns Affinity percentage (0 if not found)
   */
  getAffinityPercent(skillId: string, actionId: string): number {
    return this.getActionAffinity(skillId, actionId)?.affinityPercent ?? 0;
  }

  /**
   * Check if an action is mastered (100% affinity).
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   * @returns True if mastered
   */
  isActionMastered(skillId: string, actionId: string): boolean {
    const affinity = this.getActionAffinity(skillId, actionId);
    return affinity ? affinity.completions >= MAX_AFFINITY_COMPLETIONS : false;
  }

  // ===========================================================================
  // Actions
  // ===========================================================================

  /**
   * Add XP to a skill and handle level-ups.
   *
   * @param skillId - The skill ID
   * @param xp - XP to add
   */
  addXP(skillId: string, xp: number): void {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) return;

    const skill = character.skills[skillId];
    if (!skill?.unlocked) return;

    const oldLevel = skill.level;
    const newTotalXP = skill.totalXP + xp;
    const newLevel = getLevelFromXP(newTotalXP);

    runInAction(() => {
      skill.totalXP = newTotalXP;
      skill.level = newLevel;
      character.updatedAt = Date.now();
      character.needsSync = true;
    });

    // Handle level-up notification
    if (newLevel > oldLevel) {
      this.handleLevelUp(skillId, oldLevel, newLevel);
    }

    // Save to localStorage
    this.rootStore.characterStore['saveCharacterToLocalStorage'](character);
  }

  /**
   * Increment action completion count and update affinity.
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   */
  incrementActionAffinity(skillId: string, actionId: string): void {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) return;

    const skill = character.skills[skillId];
    if (!skill) return;

    runInAction(() => {
      // Get or create affinity
      if (!skill.actionAffinity[actionId]) {
        skill.actionAffinity[actionId] = {
          actionId,
          completions: 0,
          affinityPercent: 0,
        };
      }

      const affinity = skill.actionAffinity[actionId];

      // Increment completions (cap at max)
      if (affinity.completions < MAX_AFFINITY_COMPLETIONS) {
        affinity.completions += 1;
        affinity.affinityPercent = calculateAffinityPercent(affinity.completions);

        // Check for mastery achievement
        if (affinity.completions === MAX_AFFINITY_COMPLETIONS) {
          this.handleActionMastery(skillId, actionId);
        }
      }

      character.updatedAt = Date.now();
      character.needsSync = true;
    });

    // Save to localStorage
    this.rootStore.characterStore['saveCharacterToLocalStorage'](character);
  }

  /**
   * Start a skilling action.
   *
   * Validates requirements and sets character to skilling activity.
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   * @returns True if action started successfully
   */
  startAction(skillId: string, actionId: string): boolean {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) {
      this.rootStore.notificationStore.error('No active character');
      return false;
    }

    // Validate action can be performed
    const validation = this.validateAction(skillId, actionId);
    if (!validation.canPerform) {
      this.rootStore.notificationStore.error(validation.reason ?? 'Cannot perform action');
      return false;
    }

    // Get action definition
    const action = this.rootStore.gameStore.content.get<ActionDefinition>('actions', actionId);
    if (!action) {
      this.rootStore.notificationStore.error('Action not found');
      return false;
    }

    // Create skilling activity
    const activityData = createSkillingActivityData(skillId, actionId, action.baseTimeMs);
    const activity = createSkillingActivity(activityData);

    runInAction(() => {
      character.currentActivity = activity;
      character.updatedAt = Date.now();
      character.needsSync = true;
    });

    // Save to localStorage
    this.rootStore.characterStore['saveCharacterToLocalStorage'](character);
    return true;
  }

  /**
   * Stop current skilling action.
   */
  stopAction(): void {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character || character.currentActivity.type !== 'skilling') return;

    runInAction(() => {
      character.currentActivity = {
        type: 'idle',
        startedAt: Date.now(),
        progress: 0,
      };
      character.updatedAt = Date.now();
      character.needsSync = true;
    });

    // Save to localStorage
    this.rootStore.characterStore['saveCharacterToLocalStorage'](character);
  }

  /**
   * Unlock a skill.
   *
   * @param skillId - The skill ID to unlock
   */
  unlockSkill(skillId: string): void {
    const character = this.rootStore.characterStore.activeCharacter;
    if (!character) return;

    const skill = character.skills[skillId];
    if (!skill || skill.unlocked) return;

    runInAction(() => {
      skill.unlocked = true;
      skill.unlockedAt = Date.now();
      character.updatedAt = Date.now();
      character.needsSync = true;
    });

    // Notification
    const skillDef = this.rootStore.gameStore.content.get<SkillDefinition>('skills', skillId);
    const skillName = skillDef?.name ?? skillId;
    this.rootStore.notificationStore.success(
      `${skillName} Unlocked!`,
      'You can now train this skill.'
    );

    // Save to localStorage
    this.rootStore.characterStore['saveCharacterToLocalStorage'](character);
  }

  // ===========================================================================
  // Validation
  // ===========================================================================

  /**
   * Validate if an action can be performed.
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   * @returns Validation result
   */
  validateAction(skillId: string, actionId: string): ActionValidationResult {
    const action = this.rootStore.gameStore.content.get<ActionDefinition>('actions', actionId);
    if (!action) {
      return { canPerform: false, reason: 'Action not found' };
    }

    if (action.skillId !== skillId) {
      return { canPerform: false, reason: 'Action does not belong to this skill' };
    }

    // Check skill unlocked
    if (!this.isSkillUnlocked(skillId)) {
      return { canPerform: false, reason: 'Skill not unlocked' };
    }

    // Check level requirement
    const currentLevel = this.getSkillLevel(skillId);
    if (currentLevel < action.levelRequired) {
      return {
        canPerform: false,
        reason: `Requires level ${String(action.levelRequired)} (current: ${String(currentLevel)})`,
      };
    }

    // Additional requirements (location, items) - deferred to future milestones

    return { canPerform: true };
  }

  /**
   * Check if an action can be performed (convenience method).
   *
   * @param skillId - The skill ID
   * @param actionId - The action ID
   * @returns True if action can be performed
   */
  canPerformAction(skillId: string, actionId: string): boolean {
    return this.validateAction(skillId, actionId).canPerform;
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /**
   * Handle level-up notification.
   *
   * @param skillId - The skill that leveled up
   * @param oldLevel - Previous level
   * @param newLevel - New level
   */
  private handleLevelUp(skillId: string, oldLevel: number, newLevel: number): void {
    const skillDef = this.rootStore.gameStore.content.get<SkillDefinition>('skills', skillId);
    const skillName = skillDef?.name ?? skillId;

    // Multi-level notification
    if (newLevel - oldLevel > 1) {
      this.rootStore.notificationStore.success(
        `${skillName} Level Up!`,
        `You jumped from level ${String(oldLevel)} to ${String(newLevel)}!`
      );
    } else {
      this.rootStore.notificationStore.success(
        `${skillName} Level Up!`,
        `You are now level ${String(newLevel)}.`
      );
    }

    // Emit event for other systems
    this.rootStore.gameStore.events.emit('skill:level-up', {
      skillId,
      oldLevel,
      newLevel,
    });
  }

  /**
   * Handle action mastery achievement (100% affinity).
   *
   * @param skillId - The skill ID
   * @param actionId - The action that was mastered
   */
  private handleActionMastery(skillId: string, actionId: string): void {
    const actionDef = this.rootStore.gameStore.content.get<ActionDefinition>('actions', actionId);
    const actionName = actionDef?.name ?? actionId;

    this.rootStore.notificationStore.success(
      `Mastery Achieved!`,
      `You have mastered: ${actionName}`
    );

    // Emit event for other systems
    this.rootStore.gameStore.events.emit('skill:action-mastered', {
      skillId,
      actionId,
    });
  }

  // ===========================================================================
  // Reset
  // ===========================================================================

  /**
   * Reset the store.
   *
   * Note: Actual skill data lives in CharacterData, so reset is a no-op.
   * Skills are reset when character is deleted.
   */
  reset(): void {
    // No local state to reset - all skill data is in CharacterStore
  }
}
